import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { useNavigationStore } from '@/common/store';
import { Button } from '@/common/components/button/Button';
import { toastUtils } from '@/common/utils/toastUtils';
import { notificationService, ApplyCompanyUserInfo } from '@/services/notificationService';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { navigateBackOrClose } from '@/common/utils/commonUtil';
import Avatar from '@/common/components/avatar/Avatar';
import {
  Building2,
  Mail,
  Phone,
  UserPlus,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Shield,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  Sparkles,
  Check,
  X,
  PartyPopper,
  Ban
} from 'lucide-react';

/**
 * 조직원 가입 신청 승인/거절 페이지
 * - 단일 신청자 정보 표시
 * - 승인/거절 기능 (역할 선택 포함)
 * - 거절 시 사유 입력
 */

const JoinApplyPage: React.FC = () => {
  const {   setHeaderTitle, clearBackNavigation } = useNavigationStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // 상태 관리
  const [applyUserInfo, setApplyUserInfo] = useState<ApplyCompanyUserInfo | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const notificationId = searchParams.get('n_id');


  const getServiceTitle = (serviceType: string) => {
    switch(serviceType) {
      case 'STARTUP':
        return '스타트업 공간';
      case 'INVESTOR':
        return '투자자 공간';
      case 'INNOVATE':
        return '액셀러레이터 공간';
    }
  }

  // 가입 신청 정보 조회
  const fetchApplyUserInfo = async () => {
    if (!notificationId) {
      toastUtils.error('잘못된 접근입니다.');
      navigate('/my-space');
      return;
    }

    setIsLoading(true);
    try {
      const data = await notificationService.getApplyCompanyUser(notificationId);
      if (data) {
        setApplyUserInfo(data);
      } else {
        toastUtils.error('신청 정보를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('가입 신청 정보 조회 실패:', error);
      toastUtils.error('가입 신청 정보를 불러오는데 실패했습니다.');
      navigate('/my-space');
    } finally {
      setIsLoading(false);
    }
  };

  // 승인 처리
  const handleApprove = async () => {
    if (!notificationId) return;
    
    if (selectedRoles.length === 0) {
      toastUtils.warning('역할을 선택해주세요.');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await notificationService.acceptApplyCompanyUser(notificationId, selectedRoles);
      if (result.success) {
        setApplyUserInfo(prev => prev ? { ...prev, isAccepted: true } : null);
      } else {
        toastUtils.error(result.message || '승인 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('승인 처리 실패:', error);
      toastUtils.error('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 거절 처리
  const handleReject = async () => {
    if (!notificationId) return;
    
    if (!rejectReason.trim()) {
      toastUtils.warning('거절 사유를 입력해주세요.');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await notificationService.rejectApplyCompanyUser(notificationId, rejectReason);
      if (result.success) {
        setApplyUserInfo(prev => prev ? { ...prev, isAccepted: false, rejectReason } : null);
      } else {
        toastUtils.error(result.message || '거절 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('거절 처리 실패:', error);
      toastUtils.error('거절 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 역할 선택/해제
  const handleRoleToggle = (roleId: number) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  // 거절 폼 표시/숨기기
  const handleShowRejectForm = () => {
    setShowRejectForm(true);
  };

  const handleCancelReject = () => {
    setShowRejectForm(false);
    setRejectReason('');
  };

  useEffect(() => {
    clearBackNavigation();
    
    
    fetchApplyUserInfo();
  }, [notificationId]);

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinnerWrapper>
            <Loader2 size={48} />
          </LoadingSpinnerWrapper>
          <LoadingText>가입 신청 정보를 불러오는 중...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  if (!applyUserInfo) {
    return (
      <Container>
        <Content>
          <EmptyContainer>
            <EmptyIconWrapper>
              <AlertTriangle size={48} />
            </EmptyIconWrapper>
            <EmptyTitle>신청 정보를 찾을 수 없습니다</EmptyTitle>
            <EmptyDescription>올바르지 않은 접근이거나 이미 처리된 신청입니다.</EmptyDescription>
            <BackButton onClick={() => navigateBackOrClose(navigate, () => navigate('/space-management'))}>
              <ArrowLeft size={18} />
              돌아가기
            </BackButton>
          </EmptyContainer>
        </Content>
      </Container>
    );
  }

  // 만료된 신청인 경우
  if (applyUserInfo.isExpired) {
    return (
      <Container>
        <Content>
          <ExpiredCard>
            <ExpiredBadge>
              <AlertTriangle size={20} />
              만료됨
            </ExpiredBadge>
            
            <ExpiredHeader>
              <ExpiredIconWrapper>
                <AlertTriangle size={40} />
              </ExpiredIconWrapper>
              <ExpiredTitle>가입 신청이 만료되었습니다</ExpiredTitle>
              <ExpiredSubtitle>
                처리 기한이 지나 해당 신청이 자동으로 만료되었습니다.
              </ExpiredSubtitle>
            </ExpiredHeader>

            <ExpiredInfoGrid>
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <Building2 size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>회사</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.companyName}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
              
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <UserPlus size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>신청자</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.userName}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
              
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <Mail size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>이메일</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.userEmail}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
            </ExpiredInfoGrid>

            <ExpiredNotice>
              <AlertTriangle size={18} />
              <span>신청자에게 다시 가입 신청을 요청해주세요.</span>
            </ExpiredNotice>

            <ProcessedFooter>
              <BackButton onClick={() => navigateBackOrClose(navigate, () => navigate('/space-management'))}>
                <ArrowLeft size={18} />
                돌아가기
              </BackButton>
            </ProcessedFooter>
          </ExpiredCard>
        </Content>
      </Container>
    );
  }

  // 이미 처리된 신청인 경우 (isAccepted가 null이 아닌 경우)
  if (applyUserInfo.isAccepted !== null) {
    return (
      <Container>
        <Content>
          <ProcessedCard $isApproved={applyUserInfo.isAccepted}>
            <ProcessedBadge $isApproved={applyUserInfo.isAccepted}>
              {applyUserInfo.isAccepted ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
              {applyUserInfo.isAccepted ? '승인 완료' : '거절됨'}
            </ProcessedBadge>
            
            <ProcessedHeader>
              <ProcessedIconWrapper $isApproved={applyUserInfo.isAccepted}>
                {applyUserInfo.isAccepted ? <PartyPopper size={40} /> : <Ban size={40} />}
              </ProcessedIconWrapper>
              <ProcessedTitle>
                {applyUserInfo.isAccepted 
                  ? `${applyUserInfo.userName}님의 가입이 승인되었습니다`
                  : `${applyUserInfo.userName}님의 가입이 거절되었습니다`
                }
              </ProcessedTitle>
            </ProcessedHeader>

            <ProcessedInfoGrid>
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <Building2 size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>회사</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.companyName}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
              
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <Mail size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>이메일</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.userEmail}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
              
              <ProcessedInfoCard>
                <ProcessedInfoIcon>
                  <Phone size={20} />
                </ProcessedInfoIcon>
                <ProcessedInfoContent>
                  <ProcessedInfoLabel>전화번호</ProcessedInfoLabel>
                  <ProcessedInfoValue>{applyUserInfo.userPhone}</ProcessedInfoValue>
                </ProcessedInfoContent>
              </ProcessedInfoCard>
            </ProcessedInfoGrid>

            {!applyUserInfo.isAccepted && applyUserInfo.rejectReason && (
              <RejectReasonCard>
                <RejectReasonHeader>
                  <AlertTriangle size={18} />
                  거절 사유
                </RejectReasonHeader>
                <RejectReasonText>{applyUserInfo.rejectReason}</RejectReasonText>
              </RejectReasonCard>
            )}

            <ProcessedFooter>
              <BackButton onClick={() => navigateBackOrClose(navigate, () => navigate('/space-management'))}>
                <ArrowLeft size={18} />
                돌아가기
              </BackButton>
            </ProcessedFooter>
          </ProcessedCard>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <ApplyCard>
          {/* 헤더 섹션 */}
          <CardHeader>
            <HeaderBadge>
              <UserPlus size={16} />
              조직원 가입 신청
            </HeaderBadge>
            <CompanyBadge>
              <Building2 size={16} />
              {applyUserInfo.companyName}
            </CompanyBadge>
          </CardHeader>

          {/* 신청자 프로필 카드 */}
          <ApplicantSection>
            <ApplicantProfile>
              <ApplicantAvatarWrapper>
                <Avatar alt={applyUserInfo.userName} sizes="xl" />
                <ApplicantAvatarBadge>
                  <Sparkles size={14} />
                </ApplicantAvatarBadge>
              </ApplicantAvatarWrapper>
              <ApplicantInfo>
                <ApplicantName>{applyUserInfo.userName}</ApplicantName>
                <ApplicantMessage>
                  조직원으로 참여를 희망합니다
                </ApplicantMessage>
              </ApplicantInfo>
            </ApplicantProfile>
            
            <ContactGrid>
              <ContactCard>
                <ContactCardIcon>
                  <Mail size={18} />
                </ContactCardIcon>
                <ContactCardContent>
                  <ContactCardLabel>이메일</ContactCardLabel>
                  <ContactCardValue>{applyUserInfo.userEmail}</ContactCardValue>
                </ContactCardContent>
              </ContactCard>
              <ContactCard>
                <ContactCardIcon>
                  <Phone size={18} />
                </ContactCardIcon>
                <ContactCardContent>
                  <ContactCardLabel>전화번호</ContactCardLabel>
                  <ContactCardValue>{applyUserInfo.userPhone}</ContactCardValue>
                </ContactCardContent>
              </ContactCard>
            </ContactGrid>
          </ApplicantSection>

          {/* 거절 사유 입력 또는 권한 선택 */}
          {showRejectForm ? (
            <RejectSection>
              <RejectHeader>
                <RejectIconWrapper>
                  <XCircle size={24} />
                </RejectIconWrapper>
                <RejectTitle>거절 사유를 입력해주세요</RejectTitle>
              </RejectHeader>
              <RejectReasonInput
                placeholder="거절 사유를 상세히 입력해주세요..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <RejectActions>
                <CancelButton onClick={handleCancelReject} disabled={isProcessing}>
                  <ArrowLeft size={16} />
                  돌아가기
                </CancelButton>
                <ConfirmRejectButton 
                  onClick={handleReject} 
                  disabled={isProcessing || !rejectReason.trim()}
                >
                  {isProcessing ? <Loader2 size={18} className="spinning" /> : <X size={18} />}
                  거절 확정
                </ConfirmRejectButton>
              </RejectActions>
            </RejectSection>
          ) : (
            <>
              {/* 권한 선택 섹션 */}
              {applyUserInfo.services && applyUserInfo.services.length > 0 && (
                <PermissionSection>
                  <PermissionHeader>
                    <PermissionIconWrapper>
                      <Shield size={20} />
                    </PermissionIconWrapper>
                    <PermissionTitleGroup>
                      <PermissionTitle>서비스 권한 선택</PermissionTitle>
                      <PermissionSubtitle>부여할 권한을 선택해주세요</PermissionSubtitle>
                    </PermissionTitleGroup>
                    {selectedRoles.length > 0 && (
                      <SelectedCount>{selectedRoles.length}개 선택됨</SelectedCount>
                    )}
                  </PermissionHeader>
                  
                  <ServiceGrid $serviceCount={applyUserInfo.services.length}>
                    {applyUserInfo.services.map((service) => (
                      <ServiceCard key={service.serviceType}>
                        <ServiceCardHeader>
                          <ServiceIconWrapper>
                            <Building2 size={18} />
                          </ServiceIconWrapper>
                          <ServiceName>{getServiceTitle(service.serviceType)}</ServiceName>
                        </ServiceCardHeader>
                        <RoleList>
                          {service.roles.map((role) => (
                            <RoleItem 
                              key={role.id} 
                              onClick={() => handleRoleToggle(role.id)}
                              $selected={selectedRoles.includes(role.id)}
                            >
                              <RoleCheckWrapper $selected={selectedRoles.includes(role.id)}>
                                {selectedRoles.includes(role.id) && <Check size={14} />}
                              </RoleCheckWrapper>
                              <RoleName>{role.name}</RoleName>
                              <ChevronRight size={14} className="chevron" />
                            </RoleItem>
                          ))}
                        </RoleList>
                      </ServiceCard>
                    ))}
                  </ServiceGrid>
                </PermissionSection>
              )}

              {/* 액션 버튼 */}
              <ActionSection>
                <RejectButton onClick={handleShowRejectForm} disabled={isProcessing}>
                  <XCircle size={18} />
                  거절하기
                </RejectButton>
                <ApproveButton 
                  onClick={handleApprove}
                  disabled={isProcessing || selectedRoles.length === 0}
                >
                  {isProcessing ? (
                    <Loader2 size={18} className="spinning" />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                  승인하기
                  {selectedRoles.length === 0 && (
                    <ApproveHint>권한을 선택해주세요</ApproveHint>
                  )}
                </ApproveButton>
              </ActionSection>
            </>
          )}
        </ApplyCard>
      </Content>
    </Container>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// ============================================
// Styled Components
// ============================================

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 0%, transparent 30%);
  }
`;

const Content = styled.main`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

// 로딩 상태
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 100%;
  width: 100%;
`;

const LoadingSpinnerWrapper = styled.div`
  color: white;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

// 빈 상태
const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  text-align: center;
  padding: 48px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${fadeInUp} 0.5s ease-out;
`;

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 20px;
  color: ${semanticColors.text.onWarning.primary};
`;

const EmptyTitle = styled.h3`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  max-width: 300px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default}
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

// 처리 완료 카드
const ProcessedCard = styled.div<{ $isApproved: boolean }>`
  max-width: 600px;
  width: 100%;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${fadeInUp} 0.5s ease-out;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$isApproved 
      ? 'linear-gradient(90deg, #10b981, #34d399)' 
      : 'linear-gradient(90deg, #ef4444, #f87171)'};
  }
`;

const ProcessedBadge = styled.div<{ $isApproved: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  
  ${props => props.$isApproved ? `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  ` : `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  `}
`;

const ProcessedHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding-top: 20px;
`;

const ProcessedIconWrapper = styled.div<{ $isApproved: boolean }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  animation: ${pulse} 2s ease-in-out infinite;
  
  ${props => props.$isApproved ? `
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
    color: white;
  ` : `
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
    color: white;
  `}
`;

const ProcessedTitle = styled.h2`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const ProcessedInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProcessedInfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
`;

const ProcessedInfoIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.secondary};
  border-radius: 10px;
  color: ${semanticColors.text.brand};
`;

const ProcessedInfoContent = styled.div`
  flex: 1;
`;

const ProcessedInfoLabel = styled.span`
  display: block;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 2px;
`;

const ProcessedInfoValue = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const ProcessedFooter = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 8px;
`;

const RejectReasonCard = styled.div`
  padding: 20px;
  background: ${semanticColors.background.danger.secondary};
  border-radius: 12px;
  border-left: 4px solid ${semanticColors.text.onDanger.primary};
`;

// 만료 카드
const ExpiredCard = styled.div`
  max-width: 600px;
  width: 100%;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${fadeInUp} 0.5s ease-out;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f59e0b, #fbbf24);
  }
`;

const ExpiredBadge = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  background: ${semanticColors.background.warning.secondary};
  color: ${semanticColors.text.onWarning.primary};
`;

const ExpiredHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding-top: 20px;
`;

const ExpiredIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ExpiredTitle = styled.h2`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const ExpiredSubtitle = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  max-width: 360px;
`;

const ExpiredInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExpiredNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.onWarning.primary};
  
  svg {
    flex-shrink: 0;
  }
`;

const RejectReasonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onDanger.primary};
  margin-bottom: 8px;
`;

// 신청 카드
const ApplyCard = styled.div`
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${fadeInUp} 0.5s ease-out;
  overflow: hidden;
`;

// 카드 헤더
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const HeaderBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 100px;
  color: white;
  ${typography.caption.caption1.medium}
`;

const CompanyBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
`;

// 신청자 섹션
const ApplicantSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid ${semanticColors.border.primary};
  flex-shrink: 0;
`;

const ApplicantProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ApplicantAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ApplicantAvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: 3px solid white;
  border-radius: 50%;
  color: white;
`;

const ApplicantInfo = styled.div`
  flex: 1;
`;

const ApplicantName = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0 0 4px 0;
`;

const ApplicantMessage = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
`;

const ContactCardIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  color: ${semanticColors.text.brand};
`;

const ContactCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContactCardLabel = styled.span`
  display: block;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const ContactCardValue = styled.span`
  display: block;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 권한 선택 섹션
const PermissionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const PermissionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const PermissionIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  color: white;
`;

const PermissionTitleGroup = styled.div`
  flex: 1;
`;

const PermissionTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const PermissionSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin: 0;
`;

const SelectedCount = styled.span`
  padding: 6px 12px;
  background: ${semanticColors.background.success.secondary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onSuccess.primary};
`;

const ServiceGrid = styled.div<{ $serviceCount: number }>`
  display: grid;
  grid-template-columns: ${({ $serviceCount }) => {
    if ($serviceCount === 1) return '1fr';
    if ($serviceCount === 2) return '1fr 1fr';
    return 'repeat(auto-fit, minmax(220px, 1fr))';
  }};
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  padding: 4px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${semanticColors.border.secondary};
    border-radius: 3px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  padding: 16px;
  transition: all 0.2s ease;
  min-height: 200px;
  max-height: 100%;
  overflow: hidden;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
  }
`;

const ServiceCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${semanticColors.border.primary};
  flex-shrink: 0;
`;

const ServiceIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  color: ${semanticColors.text.brand};
`;

const ServiceName = styled.h4`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${semanticColors.border.secondary};
    border-radius: 2px;
  }
`;

const RoleItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${props => props.$selected 
    ? semanticColors.background.tertiary 
    : semanticColors.background.primary};
  border: 1px solid ${props => props.$selected 
    ? semanticColors.border.brand 
    : semanticColors.border.primary};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.tertiary};
  }
  
  .chevron {
    margin-left: auto;
    color: ${props => props.$selected 
      ? semanticColors.text.brand 
      : semanticColors.text.disabled};
    transition: all 0.2s ease;
  }
  
  &:hover .chevron {
    color: ${semanticColors.text.brand};
    transform: translateX(2px);
  }
`;

const RoleCheckWrapper = styled.div<{ $selected: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  ${props => props.$selected ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  ` : `
    background: ${semanticColors.background.secondary};
    border: 2px solid ${semanticColors.border.secondary};
  `}
`;

const RoleName = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

// 거절 섹션
const RejectSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: ${semanticColors.background.danger.secondary};
  border: 1px solid ${semanticColors.border.dangerPrimary};
  border-radius: 16px;
  flex: 1;
`;

const RejectHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RejectIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 12px;
  color: white;
`;

const RejectTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.onDanger.primary};
  margin: 0;
`;

const RejectReasonInput = styled.textarea`
  padding: 16px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  background: white;
  color: ${semanticColors.text.primary};
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${semanticColors.border.brand};
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: ${semanticColors.text.disabled};
  }
`;

const RejectActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmRejectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`;

const RejectReasonText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  margin: 0;
  line-height: 1.6;
`;

// 액션 섹션
const ActionSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid ${semanticColors.border.primary};
  flex-shrink: 0;
`;

const RejectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: white;
  border: 2px solid ${semanticColors.border.dangerPrimary};
  border-radius: 12px;
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.onDanger.secondary};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${semanticColors.background.danger.secondary};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApproveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p1.default}
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }
  
  &:disabled {
    background: ${semanticColors.background.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`;

const ApproveHint = styled.span`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
`;

export default JoinApplyPage;
